import React, { useEffect, useRef, useState } from 'react';
import { TableRow, Dropdown } from 'semantic-ui-react';
import styled, { keyframes } from 'styled-components';

import { useStudentsGetAll } from 'actions/studentsv2';
import { Heading, Table, Filters, TableCell, ActiveStatusIndicator } from 'comps';
import { getFormattedDateWithDay } from 'utils';
import Search from 'modules/list/new-search-bar';
import { Row, Separator } from '@cuemath/comps';
import Pagination from 'modules/list/pagination';
import { getOptions } from 'modules/form';

const FadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Root = styled.div`
  padding: 24px 16px;
  animation: ${FadeIn} 0.5s ease-in-out;
`;

const PaginationWrapper = styled(Row)`
  height: 42px;
`;

const StyledTableCell = styled(TableCell)`
  cursor: pointer;
`;

const TableWrapper = styled.div`
  margin-top: 24px;
`;

const isEqualList = (arr1, arr2) =>
  arr1?.length === arr2?.length && arr1?.every((val, index) => val === arr2[index]);

// const regionOptions = [
//   { key: 'INDIA', value: 'INDIA', text: 'INDIA' },
//   { key: 'USA', value: 'USA', text: 'USA' },
//   { key: 'ANY', value: 'ANY', text: 'ANY' },
// ];

function StudentList() {
  const [page, setPage] = useState(1);
  const [state, setState] = useState([]);
  // const [region, setRegion] = useState([]);
  const [grade, setGrade] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [isTestAccount, setTestAccount] = useState(false);
  const prevFilterdataRef = useRef({
    grade,
    isTestAccount,
    page,
    // region,
    search: searchKey,
    state,
  });
  const { data: students, getAll: getStudents, isProcessing } = useStudentsGetAll();
  const [filterOptions, setFilterOptions] = useState({
    page: 1,
    per_page: 13,
    student_type: 'STUDENT',
    is_demo: false,
    include_primary_course: true,
    include_primary_teacher: true,
    include_relationship_manager: true,
    is_region_policy_offender: false,
  });
  let gradeOptions = getOptions({ field: 'grade' });
  let stateOptions = getOptions({
    field: 'studentState',
  });

  useEffect(() => {
    if (!students?.length) getStudents(filterOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStudents]);

  useEffect(() => {
    const { current } = prevFilterdataRef;
    const {
      grade: prevGrade,
      isTestAccount: prevIsTestAccount,
      page: prevPage,
      // region: prevRegion,
      search: prevSearch,
      state: prevState,
    } = current || {};

    if (prevFilterdataRef && prevFilterdataRef.current) {
      let options = { ...filterOptions };
      let isFilterApplied = false;
      if (!isEqualList(prevState, state)) {
        options.state = state;
        isFilterApplied = true;
      }
      if (prevSearch !== searchKey) {
        options.search = searchKey;
        isFilterApplied = true;
      }
      if (prevPage !== page) {
        options.page = page;
        isFilterApplied = true;
      }
      if (!isEqualList(prevGrade, grade)) {
        options.grade = grade;
        isFilterApplied = true;
      }
      // if (!isEqualList(prevRegion, region)) {
      //   options.region = region;
      // isFilterApplied = true;
      // }
      if (prevIsTestAccount !== isTestAccount) {
        options.is_demo = isTestAccount;
        isFilterApplied = true;
      }
      if (isFilterApplied) {
        setFilterOptions({ ...options });
        getStudents({ ...options });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchKey, state, grade, isTestAccount]);

  const onPageChange = newPage => {
    prevFilterdataRef.current.page = page;
    setPage(newPage);
  };

  const addKeys = options => {
    const temp = options?.map(item => {
      const { label, value } = item;
      return { text: label, key: value, value };
    });

    return temp;
  };

  const onSearch = search => {
    if (search !== searchKey && (searchKey || search)) {
      prevFilterdataRef.current.search = searchKey;
      setSearchKey(search);
    }
  };

  const handleAddStateFilter = (key, data) => {
    const { value } = data || {};
    prevFilterdataRef.current.state = [...(state || [])];
    setState(value);
  };

  const handleAddGradeFilter = (key, data) => {
    const { value } = data || {};
    prevFilterdataRef.current.grade = [...(grade || [])];
    setGrade(value);
  };

  // const handleAddRegionFilter = (key, data) => {
  //   const { value } = data || {};
  //   prevFilterdataRef.current.region = [...(region || [])];
  //   setRegion(value);
  // };

  const handleFilterChange = data => {
    prevFilterdataRef.current.isTestAccount = isTestAccount;
    if (data?.includes('Test accounts')) {
      setTestAccount(true);
    } else {
      setTestAccount(false);
    }
  };

  return (
    <Root id="manual-portal-root">
      <Row justifyContent="space-between">
        <Heading>Student List</Heading>
        <Search showNewSearchBar={true} onSearch={onSearch} placeholder="Search" />
      </Row>
      <TableWrapper>
        <Filters filters={['Test accounts']} handleFilterChange={handleFilterChange} />
        <Table
          headerCell={[
            { title: 'Student name' },
            { title: 'Parent name' },
            { title: 'Teacher name' },
            { title: 'Class balance' },
            {
              title: 'grade',
              filterApplied: grade?.length,
              filterComponent: (
                <Dropdown
                  clearable
                  fluid
                  multiple
                  onChange={handleAddGradeFilter}
                  options={addKeys(gradeOptions)}
                  placeholder="Select Grade"
                  search
                  selection
                  value={grade}
                />
              ),
              showFilters: true,
            },
            { title: 'Tenure end date' },
            {
              title: 'State',
              filterApplied: state?.length,
              filterComponent: (
                <Dropdown
                  clearable
                  fluid
                  multiple
                  onChange={handleAddStateFilter}
                  options={addKeys(stateOptions)}
                  placeholder="Select State"
                  search
                  selection
                  value={state}
                />
              ),
              showFilters: true,
            },
            {
              title: 'Region',
              // filterApplied: region?.length,
              // filterComponent: (
              //   <Dropdown
              //     clearable
              //     fluid
              //     multiple
              //     onChange={handleAddRegionFilter}
              //     options={regionOptions}
              //     placeholder="Select Region"
              //     selection
              //     value={region}
              //   />
              // ),
              // showFilters: true,
            },
            { title: 'CSM name' },
          ]}
          dataLength={students?.length}
          havePagination
          showInfo={false}
          showLoader={isProcessing}
        >
          {students?.map(student => {
            const {
              // country,
              first_name: firstName,
              derived_region_text: derivedRegion,
              grade,
              id,
              last_name: lastName,
              parent_data: parentData,
              parent_name: parentName,
              primary_course: primaryCourse,
              primary_teacher_details: primaryTeacherDetails,
              state,
            } = student;
            const { rm_data: rmData } = parentData || {};
            const { last_name: rmLastName, first_name: rmFirstName } = rmData || {};
            const { name: teacherName, id: teacherId } = primaryTeacherDetails || {};
            const {
              validity_till_date: validityTillDate,
              classes_balance: classesBalance,
            } = primaryCourse || {};
            const nameText = (firstName || '') + ' ' + (lastName || '');
            const rmNameText = (rmFirstName || '') + ' ' + (rmLastName || '');

            return (
              <TableRow key={id}>
                <StyledTableCell
                  width={2}
                  onClick={() => {
                    if (!id) return () => {};
                    return window.open(`/students/${id}`, '_blank');
                  }}
                >
                  {nameText?.trim() || '-'}
                </StyledTableCell>
                <TableCell width={2}>{parentName || '-'}</TableCell>
                <StyledTableCell
                  onClick={() => {
                    if (!teacherId) return () => {};
                    return window.open(`/users/teachers/${teacherId}`, '_blank');
                  }}
                  width={2}
                >
                  {teacherName || '-'}
                </StyledTableCell>
                <TableCell width={2}>{classesBalance || '-'}</TableCell>
                <TableCell width={1}>{grade || '-'}</TableCell>
                <TableCell width={2}>
                  {validityTillDate ? getFormattedDateWithDay(validityTillDate) : '-'}
                </TableCell>
                <TableCell width={1}>
                  <ActiveStatusIndicator status={state} />
                </TableCell>
                <TableCell width={1}>{derivedRegion || '-'}</TableCell>
                <TableCell width={2}>{rmNameText?.trim() || '-'}</TableCell>
              </TableRow>
            );
          })}
        </Table>
      </TableWrapper>
      <Separator transparent height={8} />
      <PaginationWrapper justifyContent="center" alignItems="center">
        {students && (
          <Pagination
            page={page}
            perPage={13}
            count={null}
            onPageChange={onPageChange}
            shouldShowNewUi
          />
        )}
      </PaginationWrapper>
    </Root>
  );
}

export default StudentList;
